import React, { useEffect, useState, useCallback } from 'react';
import { fetchConnector, FetchPNLAnalyticsPropsFromRedux } from '../dataFetcher';
import { PNLAnalyticsEndpoint, PNLFetchStatus } from '../../actions/pnlAnalytics';
import { PNLGoodiesNonCompletedResponses } from '../PaperPlaceHolder';
import { OperationsPricesDataResp } from '../fetchTypes';
import { STANDARD_GRAPH_ELEMENT_HEIGHT } from '../../common/common';
import { postFetcher } from '../../common/fetcher';
import { getAuthUser } from '../../common/auth';
import { PNLMetaDataResp } from '../fetchTypes';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { SimpleTableConfig, TableSize, SimpleTableColumnConfig } from '../../tables/types';
import { AggFuncName } from '../../common/cellRangeAgg';
import { exposuresNumFormatter } from '../../common/exposures';
import SimpleTable from '../../tables/SimpleTable';
import { UniqueTableName } from '../../common/uniqueTableName';
import { numberFormat } from '../../containers/pivotHelper';

let columnsConfig: Array<SimpleTableColumnConfig> = [
    { name: 'category', title: '' },
    { name: 'product', title: '' },
    { name: 'sub_category', title: '' },
    { name: 'settle', title: 'SETTLE', bodyTemplate: (val) => (val != null ? numberFormat(val, 2, false, false) : '') },
    { name: 'balmo', title: 'BALMO', bodyTemplate: (val) => (val != null ? numberFormat(val, 2, false, false) : '') },
];

const config: SimpleTableConfig = {
    uniqueTableName: UniqueTableName.OperationsPrices,
    title: `Operations Prices`,
    size: TableSize.Small,
    columns: columnsConfig,
    grandTotal: false,
    aggFormatVal: (aggFuncName: AggFuncName, val: number) => {
        return aggFuncName === AggFuncName.Count ? val : exposuresNumFormatter(val);
    },
    pivotDepth: 2,
    stickyHeader: true,
};

type Props = FetchPNLAnalyticsPropsFromRedux & {};

const OperationsPrices = ({ pnlAnalytics, authUser, fetchLoadingAction, fetchCompletedAction, fetchFailedAction }: Props) => {
    const [mounted, setMounted] = useState(false);
    const pnl_list = (pnlAnalytics.meta.data! as PNLMetaDataResp).pnl_list;
    const [tableConfig, setTableConfig] = useState({
        startDate: pnl_list[pnl_list.length - 1].pipe_run_date,
        endDate: pnl_list[0].pipe_run_date,
    });

    const fetchTableData = useCallback(() => {
        postFetcher({
            url: '/pnl_analytics/operations_prices',
            data: tableConfig,
            headers: {
                'Swiss-Tech-Auth-Token': getAuthUser(authUser).swiss_token,
            },
            onStart: (cancelRequest) => {
                fetchLoadingAction({ endpoint: PNLAnalyticsEndpoint.OperationsPrices, cancelRequest });
            },
            onSuccess: (res) => {
                fetchCompletedAction({ endpoint: PNLAnalyticsEndpoint.OperationsPrices, data: res });
            },
            onFail: (err) => {
                fetchFailedAction({ endpoint: PNLAnalyticsEndpoint.OperationsPrices, err });
            },
        });
    }, [tableConfig, authUser, fetchLoadingAction, fetchCompletedAction, fetchFailedAction]);

    // Fetch table data on mount, once
    useEffect(() => {
        if (mounted) return;
        fetchTableData();
        setMounted(true);
    }, [fetchTableData, setMounted, mounted]);

    const operationsPricesData = pnlAnalytics.operations_prices;
    const respData = operationsPricesData.data! as OperationsPricesDataResp;

    return (
        <div>
            <div style={{ display: 'flex', gap: '25px', marginBottom: '12px' }}>
                <TextField
                    id="start-date"
                    label="Start Date"
                    type="date"
                    value={tableConfig.startDate}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onChange={(startDate) => {
                        setTableConfig({ ...tableConfig, startDate: startDate.target.value });
                    }}
                />
                <TextField
                    id="end-date"
                    label="End Date"
                    type="date"
                    value={tableConfig.endDate}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onChange={(endDate) => {
                        setTableConfig({ ...tableConfig, endDate: endDate.target.value });
                    }}
                />
                <Button style={{ marginLeft: '25px' }} onClick={fetchTableData} variant="contained" color="primary" size={'small'}>
                    Get Prices
                </Button>
            </div>
            {operationsPricesData.status !== PNLFetchStatus.COMPLETED ? (
                <PNLGoodiesNonCompletedResponses
                    endpoint={PNLAnalyticsEndpoint.OperationsPrices}
                    data={operationsPricesData}
                    minHeight={STANDARD_GRAPH_ELEMENT_HEIGHT}
                />
            ) : (
                <SimpleTable data={respData.operations_prices} config={config} />
            )}
        </div>
    );
};

export default fetchConnector(OperationsPrices);
